import { useState, useEffect, useMemo } from 'react';
import { Calendar, Users, Edit, Save, X, ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { shiftService } from '../services/shiftService';
import { driverService } from '../services/driverService';
import { Driver } from '../types';
import { getUser } from '../utils/storage';
import { useAppAlert } from '../components/AppAlertProvider';

interface Shift {
  id: string;
  name: string;
  code: string;
  startTime: string;
  endTime: string;
  color: string;
  type: 'daily' | 'night' | 'rotating';
}

interface ShiftAssignment {
  id: string;
  driverId: string;
  driverName?: string;
  shiftId: string;
  shiftName?: string;
  shiftColor?: string;
  date: string;
  status: 'scheduled' | 'completed' | 'absent' | 'leave';
  notes?: string;
}

export function ShiftAssignments() {
  const { showConfirm } = useAppAlert();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [assignments, setAssignments] = useState<ShiftAssignment[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedDriver, setSelectedDriver] = useState<string>('all');
  const [isAssigning, setIsAssigning] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDriverSearch, setSelectedDriverSearch] = useState('');
  const [bulkDriverSearch, setBulkDriverSearch] = useState('');
  const [showDriverFilterList, setShowDriverFilterList] = useState(false);
  const [showBulkDriverList, setShowBulkDriverList] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    driverId: '',
    shiftId: '',
    startDate: '',
    endDate: '',
    repeatDays: [] as string[],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
      try {
        const [driversRes, shiftsRes] = await Promise.all([
        driverService.getDrivers({ type: 'internal' }),
        shiftService.getShifts()
      ]);

      if (driversRes.success && driversRes.data) {
        setDrivers(driversRes.data.map((driver: any) => ({
          id: String(driver.id),
          name: driver.full_name || driver.name || '-',
          phone: driver.phone || '',
          licenseType: driver.license_class || driver.licenseType || '',
        })) as Driver[]);
      }

      if (shiftsRes.success && shiftsRes.data) {
        setShifts(shiftsRes.data);
      }

      await loadAssignments();
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

      const response = await shiftService.getAssignments({ startDate, endDate });
      if (response.success && response.data) {
        setAssignments(enrichAssignments(response.data));
      } else {
        setAssignments([]);
      }
    } catch (err: any) {
      console.error('Error loading assignments:', err);
      setAssignments([]);
    }
  };

  const toLocalDateString = (value: Date | string) => {
    const date = value instanceof Date ? value : new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const parseLocalDateInput = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  };

  const enrichAssignments = (assignmentsData: any[]) => {
    return assignmentsData.map((assignment) => {
      const normalizedAssignment: ShiftAssignment = {
        id: String(assignment.id),
        driverId: String(assignment.driverId || assignment.driver_id || ''),
        shiftId: String(assignment.shiftId || assignment.shift_id || ''),
        date: assignment.date || assignment.assignmentDate
          ? toLocalDateString(assignment.date || assignment.assignmentDate)
          : assignment.assignment_date
            ? toLocalDateString(assignment.assignment_date)
            : '',
        status: (() => {
          const rawStatus = assignment.status;
          if (rawStatus === 'planned') return 'scheduled';
          if (rawStatus === 'active') return 'scheduled';
          if (rawStatus === 'cancelled') return 'absent';
          return rawStatus || 'scheduled';
        })(),
        notes: assignment.notes || '',
      };

      const driver = drivers.find(d => d.id === normalizedAssignment.driverId);
      const shift = shifts.find(s => s.id === normalizedAssignment.shiftId);
      return {
        ...normalizedAssignment,
        driverName: assignment.driver_name || driver?.name,
        shiftName: assignment.shift_name || shift?.name,
        shiftColor: assignment.color || shift?.color,
      };
    });
  };

  const saveAssignments = async (newAssignments: ShiftAssignment[]) => {
    setAssignments(newAssignments);
  };

  const createBulkAssignments = async () => {
    if (!assignmentForm.driverId || !assignmentForm.shiftId || !assignmentForm.startDate || !assignmentForm.endDate) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }

    setIsSaving(true);
    try {
      const newAssignments: any[] = [];
      const replacedDates: string[] = [];
      const start = parseLocalDateInput(assignmentForm.startDate);
      const end = parseLocalDateInput(assignmentForm.endDate);

      for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        
        if (assignmentForm.repeatDays.length === 0 || assignmentForm.repeatDays.includes(dayName)) {
          const dateStr = toLocalDateString(date);
          
          newAssignments.push({
            driverId: assignmentForm.driverId,
            shiftId: assignmentForm.shiftId,
            date: dateStr,
            status: 'scheduled',
          });
        }
      }

      if (newAssignments.length > 0) {
        const currentUser = getUser();
        let createdCount = 0;
        for (const assignment of newAssignments) {
          const existingAssignments = assignments.filter(a => 
            a.driverId === assignment.driverId && a.date === assignment.date
          );

          if (existingAssignments.length > 0) {
            for (const existing of existingAssignments) {
              await shiftService.deleteAssignment(existing.id);
            }
            replacedDates.push(assignment.date);
          }

          const assignmentDate = new Date(assignment.date);
          const startOfYear = new Date(assignmentDate.getFullYear(), 0, 1);
          const diffDays = Math.floor((assignmentDate.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
          const weekNumber = Math.ceil((diffDays + startOfYear.getDay() + 1) / 7);

          await shiftService.createAssignment({
            driverId: assignment.driverId,
            shiftId: assignment.shiftId,
            assignmentDate: assignment.date,
            weekNumber,
            year: assignmentDate.getFullYear(),
            notes: '',
            assignedBy: currentUser?.id || 1,
          });
          createdCount += 1;
        }
        await loadAssignments();
        
        setIsAssigning(false);
        setAssignmentForm({
          driverId: '',
          shiftId: '',
          startDate: '',
          endDate: '',
          repeatDays: [],
        });
        
        let message = `${createdCount} vardiya ataması başarıyla oluşturuldu!`;
        if (replacedDates.length > 0) {
          message += `\n\n${replacedDates.length} gün için mevcut vardiya kaydı silinip yeni vardiya atandı`;
        }
        alert(message);
      } else {
        alert('Seçilen tarih aralığında atanacak gün bulunamadı.');
      }
    } catch (err: any) {
      console.error('Error creating assignments:', err);
      alert(err.message || 'Atama oluşturulurken hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteAssignment = async (id: string) => {
    if (await showConfirm({ title: 'Vardiya Atamasını Sil', message: 'Bu vardiya atamasını silmek istediğinizden emin misiniz?', confirmText: 'Sil', tone: 'warning' })) {
      try {
        const response = await shiftService.deleteAssignment(id);
        if (response.success) {
          await loadAssignments();
        }
      } catch (err: any) {
        console.error('Error deleting assignment:', err);
        alert(err.message || 'Silme sırasında hata oluştu');
      }
    }
  };

  const updateAssignmentStatus = async (id: string, status: ShiftAssignment['status']) => {
    try {
      const response = await shiftService.updateAssignment(id, { status });
      if (response.success) {
        await loadAssignments();
      }
    } catch (err: any) {
      console.error('Error updating assignment:', err);
      alert(err.message || 'Güncelleme sırasında hata oluştu');
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];

    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }

    return days;
  };

  const getAssignmentsForDriverAndDate = (driverId: string, date: Date) => {
    const dateStr = toLocalDateString(date);
    return assignments.filter(a => 
      a.driverId === driverId && a.date === dateStr
    );
  };

  const goToPreviousMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1));
  };

  useEffect(() => {
    if (!isLoading) {
      loadAssignments();
    }
  }, [selectedMonth]);

  const toggleDay = (day: string) => {
    const current = assignmentForm.repeatDays;
    if (current.includes(day)) {
      setAssignmentForm({ ...assignmentForm, repeatDays: current.filter(d => d !== day) });
    } else {
      setAssignmentForm({ ...assignmentForm, repeatDays: [...current, day] });
    }
  };

  const filteredDrivers = drivers.filter(d => {
    if (selectedDriver !== 'all' && d.id !== selectedDriver) return false;
    return true;
  });

  const searchableDrivers = useMemo(() => {
    const q = selectedDriverSearch.trim().toLocaleLowerCase('tr-TR');
    if (!q) return drivers;
    return drivers.filter((driver) => (`${driver.name} ${driver.phone || ''}`).toLocaleLowerCase('tr-TR').includes(q));
  }, [drivers, selectedDriverSearch]);

  const searchableBulkDrivers = useMemo(() => {
    const q = bulkDriverSearch.trim().toLocaleLowerCase('tr-TR');
    if (!q) return drivers;
    return drivers.filter((driver) => (`${driver.name} ${driver.phone || ''}`).toLocaleLowerCase('tr-TR').includes(q));
  }, [drivers, bulkDriverSearch]);

  const days = getDaysInMonth(selectedMonth);
  const monthName = selectedMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });

  const WEEKDAYS = [
    { value: 'Monday', label: 'Pzt' },
    { value: 'Tuesday', label: 'Sal' },
    { value: 'Wednesday', label: 'Çar' },
    { value: 'Thursday', label: 'Per' },
    { value: 'Friday', label: 'Cum' },
    { value: 'Saturday', label: 'Cmt' },
    { value: 'Sunday', label: 'Paz' },
  ];

  const getStatusBadge = (status: ShiftAssignment['status']) => {
    const statuses = {
      scheduled: { label: 'Planlandı', color: 'bg-blue-100 text-blue-800' },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
      absent: { label: 'Gelmedi', color: 'bg-red-100 text-red-800' },
      leave: { label: 'İzinli', color: 'bg-yellow-100 text-yellow-800' },
    };
    return statuses[status];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-500">Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-red-200">
        <div className="flex flex-col items-center gap-3 p-8">
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vardiya Atamaları</h1>
          <p className="text-gray-500 mt-1">Personel ve Sürücülere vardiya planlayın</p>
        </div>
        <button
          onClick={() => setIsAssigning(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Calendar className="w-5 h-5" />
          Toplu Vardiya Ata
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sürücü</label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={selectedDriver === 'all' ? selectedDriverSearch : (drivers.find(d => d.id === selectedDriver)?.name || selectedDriverSearch)}
                  onChange={(e) => {
                    setSelectedDriverSearch(e.target.value);
                    setSelectedDriver('all');
                    setShowDriverFilterList(true);
                  }}
                  onFocus={() => setShowDriverFilterList(true)}
                  placeholder="Sürücü ara veya seç"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {showDriverFilterList && (
                <div className="absolute z-20 mt-2 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                  <button type="button" onClick={() => { setSelectedDriver('all'); setSelectedDriverSearch(''); setShowDriverFilterList(false); }} className="block w-full px-4 py-2 text-left hover:bg-gray-50">Tüm Sürücüler</button>
                  {searchableDrivers.map((d) => (
                    <button key={d.id} type="button" onClick={() => { setSelectedDriver(d.id); setSelectedDriverSearch(d.name); setShowDriverFilterList(false); }} className="block w-full px-4 py-2 text-left hover:bg-gray-50">
                      <div className="font-medium text-gray-900">{d.name}</div>
                      <div className="text-xs text-gray-500">{d.phone || '-'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ay</label>
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousMonth}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 text-center font-medium">
                {monthName}
              </div>
              <button
                onClick={goToNextMonth}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 bg-gray-50 sticky left-0">
                Personel
              </th>
              {days.map((day, index) => (
                <th
                  key={index}
                  className={`px-2 py-3 text-center text-xs font-medium ${
                    day.getDay() === 0 || day.getDay() === 6
                      ? 'bg-red-50 text-red-900'
                      : 'bg-gray-50 text-gray-700'
                  }`}
                >
                  <div>{day.getDate()}</div>
                  <div className="text-xs opacity-70">
                    {day.toLocaleDateString('tr-TR', { weekday: 'short' })}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredDrivers.map((driver) => (
              <tr key={driver.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 sticky left-0 bg-white border-r border-gray-200">
                  <div className="font-medium text-sm text-gray-900">{driver.name}</div>
                  <div className="text-xs text-gray-500">{driver.licenseType}</div>
                </td>
                {days.map((day, index) => {
                  const dayAssignments = getAssignmentsForDriverAndDate(driver.id, day);
                  return (
                    <td
                      key={index}
                      className={`px-1 py-1 text-center relative ${
                        day.getDay() === 0 || day.getDay() === 6 ? 'bg-gray-50' : ''
                      }`}
                    >
                      {dayAssignments.map((assignment) => (
                        <div
                          key={assignment.id}
                          className="relative"
                        >
                          <div
                            onClick={() => setOpenMenuId(openMenuId === assignment.id ? null : assignment.id)}
                            className="text-xs font-medium px-2 py-1 rounded cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ 
                              backgroundColor: assignment.shiftColor + '30',
                              color: assignment.shiftColor,
                              border: `1px solid ${assignment.shiftColor}60`
                            }}
                          >
                            {assignment.shiftName?.split(' ')[0]}
                          </div>
                          
                          {openMenuId === assignment.id && (
                            <>
                              <div 
                                className="fixed inset-0 z-40" 
                                onClick={() => setOpenMenuId(null)}
                              />
                              
                              <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-2 min-w-[160px]">
                                <div className="text-xs font-semibold mb-2 pb-2 border-b border-gray-200">
                                  {assignment.shiftName}
                                </div>
                                <div className="flex flex-col gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateAssignmentStatus(assignment.id, 'completed');
                                      setOpenMenuId(null);
                                    }}
                                    className="text-xs px-3 py-2 bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors text-left font-medium"
                                  >
                                    ✓ Tamamlandı
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateAssignmentStatus(assignment.id, 'absent');
                                      setOpenMenuId(null);
                                    }}
                                    className="text-xs px-3 py-2 bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors text-left font-medium"
                                  >
                                    ✗ Gelmedi
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateAssignmentStatus(assignment.id, 'leave');
                                      setOpenMenuId(null);
                                    }}
                                    className="text-xs px-3 py-2 bg-yellow-50 text-yellow-700 rounded hover:bg-yellow-100 transition-colors text-left font-medium"
                                  >
                                    🏖 İzinli
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteAssignment(assignment.id);
                                      setOpenMenuId(null);
                                    }}
                                    className="text-xs px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-left font-medium"
                                  >
                                    🗑 Sil
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredDrivers.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Personel bulunamadı</p>
        </div>
      )}

      {/* Bulk Assignment Modal */}
      {isAssigning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">Toplu Vardiya Ataması</h2>
              <button onClick={() => setIsAssigning(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sürücü Seç *
                </label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      value={assignmentForm.driverId ? (drivers.find((d) => d.id === assignmentForm.driverId)?.name || bulkDriverSearch) : bulkDriverSearch}
                      onChange={(e) => {
                        setBulkDriverSearch(e.target.value);
                        setAssignmentForm({ ...assignmentForm, driverId: '' });
                        setShowBulkDriverList(true);
                      }}
                      onFocus={() => setShowBulkDriverList(true)}
                      placeholder="Sürücü ara veya seç"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {showBulkDriverList && (
                    <div className="absolute z-20 mt-2 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {searchableBulkDrivers.map((d) => (
                        <button key={d.id} type="button" onClick={() => { setAssignmentForm({ ...assignmentForm, driverId: d.id }); setBulkDriverSearch(d.name); setShowBulkDriverList(false); }} className="block w-full px-4 py-2 text-left hover:bg-gray-50">
                          <div className="font-medium text-gray-900">{d.name}</div>
                          <div className="text-xs text-gray-500">{d.phone || '-'}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vardiya Seç *
                </label>
                <select
                  value={assignmentForm.shiftId}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, shiftId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Vardiya Seçin</option>
                  {shifts.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.startTime} - {s.endTime})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlangıç Tarihi *
                  </label>
                  <input
                    type="date"
                    value={assignmentForm.startDate}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, startDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bitiş Tarihi *
                  </label>
                  <input
                    type="date"
                    value={assignmentForm.endDate}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hangi Günlerde? (Boş bırakırsanız her gün atar)
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {WEEKDAYS.map(day => (
                    <label
                      key={day.value}
                      className={`flex items-center justify-center px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                        assignmentForm.repeatDays.includes(day.value)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={assignmentForm.repeatDays.includes(day.value)}
                        onChange={() => toggleDay(day.value)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setIsAssigning(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  İptal
                </button>
                <button
                  onClick={createBulkAssignments}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Save className="w-4 h-4" />
                  Atamaları Oluştur
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Vardiyalar</h3>
        <div className="flex flex-wrap gap-4">
          {shifts.map(shift => (
            <div key={shift.id} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: shift.color }}
              />
              <span className="text-sm text-gray-700">
                {shift.name} ({shift.startTime} - {shift.endTime})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
